import { useState, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { SlidersHorizontal, AlertTriangle, Loader2, Download, Plus } from 'lucide-react';
import Swal from 'sweetalert2';
import { EditActionBar } from '../shared/EditActionBar';
import type { Driver04Data, PeakRevenueRow } from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { fetchExportConductRiskDriverXlsx } from '../../../api/functionApi';
import { formatHighlights } from '../../../utils/Format';

/* ─── Helpers ────────────────────────────────────────────── */
const fmtE = (n: number) =>
  new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(n) + ' €';

const numCls = 'w-24 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-[#1B365D]/50 tabular-nums';
const txtCls = 'w-full min-w-[7rem] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50';

type TabKey = 'flagged' | 'all';

const emptyPeakRevenueRow = (): PeakRevenueRow => ({
  branch_name:       '',
  clone_uo:          '',
  area:              '',
  regional_division: '',
  q1:                0,
  q2:                0,
  q3:                0,
  q4:                0,
  total:             0,
  arithmetic_mean:   0,
  delta_q4_vs_mean:  0,
});

/* ─── Main ───────────────────────────────────────────────── */
const FocusPicchiRicaviContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver04Data>(reportId, 4);

  const [data,        setData]        = useState<Driver04Data | null>(null);
  const [savedData,   setSavedData]   = useState<Driver04Data | null>(null);
  const [highlights,  setHighlights]  = useState<string | null>(null);
  const [savedHl,     setSavedHl]     = useState<string | null>(null);
  const [isEditing,   setIsEditing]   = useState(false);
  const [tab,         setTab]         = useState<TabKey>('flagged');
  const [soglia,      setSoglia]      = useState<number>(0);
  const [autoSaving,  setAutoSaving]  = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // La soglia iniziale viene letta dal BE una sola volta al mount. I refetch
  // successivi non devono sovrascriverla, altrimenti se il BE non persiste
  // il nuovo valore la UI torna al vecchio filtro (tabella "bloccata").
  const initializedRef = useRef(false);

  const handleExportXlsx = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const blob = await fetchExportConductRiskDriverXlsx(reportId, 'Driver04');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Focus_Picchi_Ricavi_${data?.period ?? 'report'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Export fallito', text: err?.message ?? 'Impossibile scaricare il file XLSX.' });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (!remoteData) return;
    // Difensivo: il BE puo' restituire null; normalizzo a [].
    const normalized: Driver04Data = {
      ...remoteData,
      source_rows:  remoteData.source_rows  ?? [],
      flagged_rows: remoteData.flagged_rows ?? [],
    };
    setData(normalized);
    setSavedData(normalized);
    if (!initializedRef.current) {
      setSoglia(remoteData.thresholds.deviation_pct);
      initializedRef.current = true;
    }
  }, [remoteData]);

  // Autosave della soglia (debounced ~600ms). Attivo solo quando:
  //  - NON sei in editing (per non collidere col Salva manuale);
  //  - il report NON e' read-only.
  useEffect(() => {
    if (!data || !savedData || isEditing || isReadOnly) return;
    if (soglia === savedData.thresholds.deviation_pct) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const payload: Driver04Data = { ...data, thresholds: { deviation_pct: soglia } };
      setAutoSaving(true);
      try {
        await save(payload);
        setSavedData(payload);
      } catch (err) {
        console.error('Autosave soglia fallito:', err);
        setSoglia(savedData.thresholds.deviation_pct); // rollback in UI
      } finally {
        setAutoSaving(false);
      }
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [soglia, savedData, isEditing, isReadOnly, data, save]);

  // Il BE ora restituisce due liste: `source_rows` con TUTTE le filiali
  // analizzate e `flagged_rows` con solo quelle con picco IV trimestre
  // (calcolo delta_q4_vs_mean vs soglia lato BE). KPI e Highlights restano
  // ancorati alle flagged; la tabella mostra flagged o tutte in base al tab.
  const flaggedRows: PeakRevenueRow[] = useMemo(() => {
    if (!data) return [];
    return [...data.flagged_rows].sort(
      (a, b) => Math.abs(b.delta_q4_vs_mean) - Math.abs(a.delta_q4_vs_mean),
    );
  }, [data]);

  // Righe mostrate in tabella: flaggate (default) o TUTTE le analizzate.
  // Stesso ordinamento per scarto assoluto decrescente.
  const displayRows: PeakRevenueRow[] = useMemo(() => {
    if (!data) return [];
    const src = tab === 'flagged' ? data.flagged_rows : data.source_rows;
    return [...src].sort(
      (a, b) => Math.abs(b.delta_q4_vs_mean) - Math.abs(a.delta_q4_vs_mean),
    );
  }, [data, tab]);

  const drCounts = flaggedRows.reduce<Record<string, number>>((acc, r) => {
    acc[r.regional_division] = (acc[r.regional_division] || 0) + 1;
    return acc;
  }, {});
  const topDR = Object.entries(drCounts).sort((a, b) => b[1] - a[1])[0] as [string, number] | undefined;

  const computeHl = (): string => {
    const base = `Con soglia al ${soglia}%, vengono identificate ${flaggedRows.length} ${flaggedRows.length === 1 ? 'filiale' : 'filiali'} con scarto del IV trimestre rispetto alla media degli altri trimestri superiore alla soglia impostata.`;
    if (flaggedRows[0]) {
      const topDelta = Math.abs(flaggedRows[0].delta_q4_vs_mean);
      return base + ` La filiale con lo scarto assoluto più elevato è ${flaggedRows[0].branch_name} (${fmtE(Math.round(topDelta))}). Questo andamento può segnalare una concentrazione temporale anomala delle operazioni che merita approfondimento ai fini conduct risk.`;
    }
    return base;
  };

  const handleProcedi = () => {
    if (highlights === null) setHighlights(computeHl());
    setIsEditing(true);
  };
  const handleAnnulla = () => { if (savedData) setData(savedData); setHighlights(savedHl); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setSavedHl(highlights);
    setIsEditing(false);
  };

  // Matching per clone_uo (identificatore stabile, non editabile): la riga
  // compare in entrambe le liste sorgente e va aggiornata in ognuna.
  type PeakNumField = 'q1' | 'q2' | 'q3' | 'q4' | 'total' | 'arithmetic_mean' | 'delta_q4_vs_mean';
  type PeakTextField = 'branch_name' | 'area' | 'regional_division';

  const updateRow = (cloneUo: string, key: PeakNumField, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const patch = (rs: PeakRevenueRow[]) =>
        rs.map(r => r.clone_uo === cloneUo ? { ...r, [key]: val } : r);
      return { ...prev, source_rows: patch(prev.source_rows), flagged_rows: patch(prev.flagged_rows) };
    });
  };

  const updateRowText = (cloneUo: string, key: PeakTextField, value: string) => {
    setData(prev => {
      if (!prev) return prev;
      const patch = (rs: PeakRevenueRow[]) =>
        rs.map(r => r.clone_uo === cloneUo ? { ...r, [key]: value } : r);
      return { ...prev, source_rows: patch(prev.source_rows), flagged_rows: patch(prev.flagged_rows) };
    });
  };

  /** Appende una riga vuota all'array sottostante del tab corrente. */
  const addRow = () => {
    setData(prev => {
      if (!prev) return prev;
      return tab === 'flagged'
        ? { ...prev, flagged_rows: [...prev.flagged_rows, emptyPeakRevenueRow()] }
        : { ...prev, source_rows: [...prev.source_rows, emptyPeakRevenueRow()] };
    });
  };

  const displayHl = highlights ?? (flaggedRows.length > 0 ? computeHl() : null);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-[#1B365D] rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  return (
    <div className="p-6 space-y-5">

      {/* Soglia control + edit bar */}
      <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
        <div className="w-8 h-8 rounded-lg bg-[#1B365D]/5 flex items-center justify-center shrink-0">
          <SlidersHorizontal className="w-4 h-4 text-[#1B365D]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-[#1B365D]">Soglia scarto accettabile</p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Filiali flaggate quando il IV trimestre supera la media trimestrale di oltre il valore impostato (default {data.thresholds.deviation_pct}%)
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {autoSaving && (
            <Loader2 className="w-3.5 h-3.5 text-[#1B365D]/60 animate-spin" />
          )}
          <input
            type="number"
            min={1}
            max={200}
            value={soglia}
            disabled={isEditing || isReadOnly}
            onChange={e => {
              const val = parseFloat(e.target.value);
              if (!isNaN(val)) setSoglia(Math.max(1, Math.min(200, val)));
            }}
            className="w-16 text-right text-sm font-bold text-[#1B365D] border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#1B365D]/40 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <span className="text-xs font-medium text-gray-400">%</span>
        </div>
        <button
          onClick={handleExportXlsx}
          disabled={isExporting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#1B365D] border border-[#1B365D]/20 hover:bg-[#1B365D]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-3.5 h-3.5" />
          {isExporting ? 'Esportazione…' : 'Scarica XLSX'}
        </button>
        <EditActionBar isEditing={isEditing} onProcedi={handleProcedi} onAnnulla={handleAnnulla} onSalva={handleSalva} disabled={isReadOnly} />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-amber-600">{flaggedRows.length}</div>
          <div className="text-[11px] text-gray-400 mt-1">Filiali con picco IV trim.</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{soglia}%</div>
          <div className="text-[11px] text-gray-400 mt-1">Soglia applicata</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          {topDR ? (
            <>
              <div className="text-sm font-bold text-[#1B365D] leading-snug break-words">
                {topDR[0].replace(/^Direzione Regionale /, '').replace(/^Direzione Commerciale /, '')}
              </div>
              <div className="text-[11px] text-gray-400 mt-1">DR con più picchi ({topDR[1]})</div>
            </>
          ) : (
            <div className="text-2xl font-bold text-gray-300">—</div>
          )}
        </div>
      </div>

      {/* Empty state */}
      {flaggedRows.length === 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 border border-gray-100">
          <AlertTriangle className="w-4 h-4 text-gray-400 shrink-0" />
          <p className="text-xs text-gray-500">
            Nessuna filiale flaggata per il periodo selezionato.
          </p>
        </div>
      )}

      {/* Highlights */}
      {displayHl && (
        <div className="px-4 py-3 rounded-xl bg-[#1B365D]/5 border border-[#1B365D]/10 text-xs text-[#1B365D]/80 leading-relaxed">
          <span className="font-semibold">Highlights: </span>
          {isEditing
            ? <textarea className="w-full mt-1 p-2 text-xs text-[#1B365D]/80 leading-relaxed border border-[#1B365D]/20 rounded-lg resize-y focus:outline-none focus:border-[#1B365D]/40 bg-white/70 min-h-[80px]" value={displayHl} onChange={e => setHighlights(e.target.value)} />
            : formatHighlights(displayHl)}
        </div>
      )}

      {/* Tabs: solo flaggate vs tutte le analizzate */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
        <button
          onClick={() => setTab('flagged')}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
            tab === 'flagged' ? 'bg-white text-amber-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Con picco IV trim.
          <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
            {data.flagged_rows.length}
          </span>
        </button>
        <button
          onClick={() => setTab('all')}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
            tab === 'all' ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Tutte le analizzate
          <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-200 text-gray-700">
            {data.source_rows.length}
          </span>
        </button>
      </div>

      {/* Table */}
      {displayRows.length > 0 ? (
        <>
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-xs font-semibold text-[#1B365D]">
              {tab === 'flagged'
                ? `Filiali con picco IV trimestre — soglia ${soglia}% · ${data.period}`
                : `Tutte le filiali analizzate · ${data.period}`}
            </p>
            <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${
              tab === 'flagged'
                ? 'text-amber-600 bg-amber-50 border-amber-100'
                : 'text-[#1B365D] bg-[#1B365D]/5 border-[#1B365D]/10'
            }`}>
              {displayRows.length} filiali {tab === 'flagged' ? 'identificate' : 'analizzate'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                  <th className="text-left  px-4 py-2.5 font-semibold">Filiale</th>
                  <th className="text-left  px-4 py-2.5 font-semibold">Area</th>
                  <th className="text-left  px-4 py-2.5 font-semibold">Dir. Regionale</th>
                  <th className="text-right px-4 py-2.5 font-semibold">I Trim.</th>
                  <th className="text-right px-4 py-2.5 font-semibold">II Trim.</th>
                  <th className="text-right px-4 py-2.5 font-semibold">III Trim.</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-amber-500">IV Trim.</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Totale</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Media trim.</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-amber-500">|IV − Media|</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {displayRows.map((row, i) => (
                  <motion.tr
                    key={row.clone_uo}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.02, 0.4) }}
                    className="hover:bg-amber-50/30 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-[#1B365D] whitespace-nowrap" title={`CloneUo: ${row.clone_uo}`}>
                      {isEditing ? <input type="text" className={txtCls} value={row.branch_name} onChange={e => updateRowText(row.clone_uo, 'branch_name', e.target.value)} /> : row.branch_name}
                    </td>
                    <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.area} onChange={e => updateRowText(row.clone_uo, 'area', e.target.value)} /> : row.area}
                    </td>
                    <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.regional_division} onChange={e => updateRowText(row.clone_uo, 'regional_division', e.target.value)} /> : row.regional_division}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-400 whitespace-nowrap">
                      {isEditing ? <input type="number" className={numCls} value={row.q1} onChange={e => updateRow(row.clone_uo, 'q1', e.target.value)} /> : fmtE(row.q1)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-400 whitespace-nowrap">
                      {isEditing ? <input type="number" className={numCls} value={row.q2} onChange={e => updateRow(row.clone_uo, 'q2', e.target.value)} /> : fmtE(row.q2)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-400 whitespace-nowrap">
                      {isEditing ? <input type="number" className={numCls} value={row.q3} onChange={e => updateRow(row.clone_uo, 'q3', e.target.value)} /> : fmtE(row.q3)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-amber-700 whitespace-nowrap bg-amber-50/60">
                      {isEditing ? <input type="number" className={numCls} value={row.q4} onChange={e => updateRow(row.clone_uo, 'q4', e.target.value)} /> : fmtE(row.q4)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">
                      {isEditing ? <input type="number" className={numCls} value={row.total} onChange={e => updateRow(row.clone_uo, 'total', e.target.value)} /> : fmtE(row.total)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">
                      {isEditing ? <input type="number" className={numCls} value={row.arithmetic_mean} onChange={e => updateRow(row.clone_uo, 'arithmetic_mean', e.target.value)} /> : fmtE(Math.round(row.arithmetic_mean))}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isEditing
                        ? <input type="number" className={numCls} value={row.delta_q4_vs_mean} onChange={e => updateRow(row.clone_uo, 'delta_q4_vs_mean', e.target.value)} />
                        : <span className="inline-flex px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-amber-700 font-bold text-[10px]">
                            {fmtE(Math.round(Math.abs(row.delta_q4_vs_mean)))}
                          </span>}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {isEditing && (
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={() => addRow()}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
            </button>
          </div>
        )}
        </>
      ) : tab === 'all' ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-400">
          Nessuna filiale analizzata disponibile.
        </div>
      ) : null}
    </div>
  );
};

export default FocusPicchiRicaviContainer;
